// apps/patient/src/pages/appointments/ClaimOfferPage.tsx
// Public "an appointment opened up" page reached from a waitlist offer text or
// email (/w/:token). Standalone (no PatientLayout, no auth). First to claim gets
// the slot; the token is only ever passed to the API.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, CalendarClock, Check, Clock, Loader2, MapPin, Phone, User } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { patientApiService } from '../../services/api';
import { WaitlistOfferInfo, formatLocationAddress, formatProviderName, readErrorMessage } from '../../components/appointments/manageTypes';

const INVALID_LINK_MESSAGE = 'This link is no longer valid. Please contact the office.';

const UNAVAILABLE_COPY: Record<string, { title: string; body: string }> = {
  filled: { title: 'This time has been taken', body: 'Someone else on the waitlist claimed it first. You are still on the waitlist and we will let you know when another time opens up.' },
  expired: { title: 'This offer has passed', body: 'The appointment time is no longer in the future. You are still on the waitlist.' },
  unavailable: { title: 'This offer is no longer open', body: 'Your waitlist entry is no longer active. Call the office if you would like to be added again.' },
};

export function ClaimOfferPage() {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<WaitlistOfferInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError(INVALID_LINK_MESSAGE);
      setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      try {
        const result = await patientApiService.getWaitlistOffer(token);
        if (active) setInfo(result);
      } catch {
        if (active) setError(INVALID_LINK_MESSAGE);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const handleClaim = async () => {
    if (!token) return;
    setClaiming(true);
    setClaimError(null);
    try {
      const result = await patientApiService.claimWaitlistOffer(token);
      setInfo(result);
    } catch (err) {
      const message = await readErrorMessage(err, 'We could not book this time. Please try again or call the office.');
      setClaimError(message);
      // The slot may have just been taken: refresh the derived state so the page reflects it
      try {
        const fresh = await patientApiService.getWaitlistOffer(token);
        setInfo(fresh);
      } catch {
        // keep the current view
      }
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-gray-600">Loading this appointment time...</p>
        </div>
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md text-center p-8">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load This Offer</h1>
          <p className="text-gray-600">{error || INVALID_LINK_MESSAGE}</p>
        </div>
      </div>
    );
  }

  const startDate = new Date(info.start_timestamp);
  const dateText = formatInTimeZone(startDate, info.timezone, 'EEEE, MMMM d, yyyy');
  const timeText = formatInTimeZone(startDate, info.timezone, 'h:mm a zzz');
  const providerName = formatProviderName(info.provider);
  const phone = info.organization_phone;
  const unavailable = !info.claimed && !info.can_claim ? (UNAVAILABLE_COPY[info.reason] || UNAVAILABLE_COPY.filled) : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-lg mx-auto px-4 py-8 space-y-4">
        <div className="text-center">
          <p className="text-sm text-gray-500">{info.organization_name}</p>
          <h1 className="text-xl font-semibold text-gray-900 mt-1">
            {info.claimed ? 'You Are Booked' : 'An Appointment Opened Up'}
          </h1>
        </div>

        {info.claimed && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <Check className="h-10 w-10 text-green-600 mx-auto mb-3" />
            <p className="text-green-900 font-medium">
              Your appointment on {dateText} at {timeText} is confirmed.
            </p>
            <p className="text-sm text-green-800 mt-3">A confirmation is on its way to you.</p>
          </div>
        )}

        {unavailable && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
            <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
            <p className="text-amber-900 font-medium">{unavailable.title}</p>
            <p className="text-sm text-amber-800 mt-3">{unavailable.body}</p>
            {phone && (
              <p className="text-sm text-amber-800 mt-3">
                Questions? Call {info.organization_name} at{' '}
                <a href={`tel:${phone}`} className="underline font-medium">{phone}</a>.
              </p>
            )}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-3">
          <div className="flex items-center gap-3">
            <CalendarClock className="h-5 w-5 text-gray-400" />
            <span className="font-medium text-gray-900">{dateText}</span>
          </div>
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-gray-400" />
            <span className="text-gray-800">{timeText} ({info.duration_minutes} minutes)</span>
          </div>
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-gray-400" />
            <span className="text-gray-800">{providerName}{info.appointment_type_name ? ` for ${info.appointment_type_name}` : ''}</span>
          </div>
          {info.location && (
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-gray-800">{info.location.name}</p>
                {formatLocationAddress(info.location.address) && (
                  <p className="text-sm text-gray-500">{formatLocationAddress(info.location.address)}</p>
                )}
              </div>
            </div>
          )}
          {phone && (
            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-gray-400" />
              <a href={`tel:${phone}`} className="text-primary hover:underline">{phone}</a>
            </div>
          )}
        </div>

        {!info.claimed && info.can_claim && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 text-center">
              This time is offered to several people on the waitlist. The first to claim it gets it.
            </p>
            {claimError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">{claimError}</div>
            )}
            <button
              type="button"
              onClick={handleClaim}
              disabled={claiming}
              className="w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {claiming && <Loader2 className="h-4 w-4 animate-spin" />}
              {claiming ? 'Booking...' : 'Book this time'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
